import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./styles.module.css";
import "./App.css";
import enGB from "date-fns/locale/en-GB";

// ICONS & BRAND
import alertIcon from "./Assets/alert.svg";
import filterOutlineIcon from "./Assets/filter-outline.svg";
import filterSolidIcon from "./Assets/filter-solid.svg";
import settingsIcon from "./Assets/settings.svg";
import eye from "./Assets/eye.svg"; // same icon used on Login

// PANELS
import FilterPanel, { DEFAULT_FILTERS } from "./FilterPanel";
import AlertManager from "./AlertManager";
import SettingsPanel from "./SettingsPanel";
import CameraPanel from "./CameraPanel";
import TagPanel from "./TagPanel";
import UserPanel from "./UserPanel";
import { getSessionUser, logout as doLogout } from "./Auth";

registerLocale("en-GB", enGB);

// ────────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────────
const sameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const addMinutes = (d, mins) => new Date(d.getTime() + mins * 60000);
const toDate = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

function getPageWindow(currentPage, totalPages, maxButtons = 9) {
  if (totalPages <= maxButtons) return { start: 1, end: totalPages };
  const effectiveMax = maxButtons - 1;
  const left = Math.floor(effectiveMax / 2);
  const right = effectiveMax - left;
  let start = currentPage - left;
  let end = currentPage + right;
  if (start < 2) {
    start = 2;
    end = 1 + effectiveMax;
  }
  if (end > totalPages) {
    end = totalPages;
    start = totalPages - effectiveMax;
  }
  return { start, end };
}

const isPanelFiltersActive = (f) =>
  !!f &&
  ((f.severity && f.severity !== "any") ||
    (f.dayNight && f.dayNight !== "any") ||
    (Array.isArray(f.tagKeys) && f.tagKeys.length > 0) ||
    (Array.isArray(f.makes) && f.makes.length > 0) ||
    !!f.alertOnly);

// Small, dependency-free media query hook (avoids layout thrash on Safari)
function useBreakpoint() {
  const [w, setW] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1280));
  useEffect(() => {
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setW(window.innerWidth));
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);
  // breakpoints roughly: ≥1024 full, 640–1023 medium, <640 compact
  return useMemo(() => ({
    compact: w < 640,
    medium: w >= 640 && w < 1200,
    full: w >= 1200,
    width: w,
  }), [w]);
}

// ────────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────────
const Pagination = ({
  inCurrentPage,
  inCurrentVRMFilter,
  pageChangeHandler,
  vrmFilterChangeHandler,
  totalRows,
  rowsPerPage,
  dateRangeChangeHandler,
  inStartDateTime,
  inEndDateTime,
  inCurrentFilters,
  filtersChangeHandler,
}) => {
  const safeRowsPerPage = Math.max(1, rowsPerPage || 10);
  const safeTotalRows = Math.max(0, totalRows || 0);
  const noOfPages = Math.max(1, Math.ceil(safeTotalRows / safeRowsPerPage));

  const [currentPage, setCurrentPage] = useState(inCurrentPage);
  const [start, setStart] = useState(() => toDate(inStartDateTime));
  const [end, setEnd] = useState(() => toDate(inEndDateTime));

  // Panels
  const filterBtnRef = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentFilters, setCurrentFilters] = useState(inCurrentFilters || DEFAULT_FILTERS);

  const [showAlertMgr, setShowAlertMgr] = useState(false);
  const alertBtnRef = useRef(null);

  const [showSettings, setShowSettings] = useState(false);
  const [showCameras, setShowCameras] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showTags, setShowTags] = useState(false);

  const settingsBtnRef = useRef(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // user/admin
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const u = await getSessionUser();
        if (!active) return;
        window.__appUser = u;
        const admin = u && (u.is_admin === 1 || u.is_admin === "1" || u.is_admin === true);
        setIsAdmin(!!admin);
      } catch {
        if (!active) return;
        window.__appUser = null;
        setIsAdmin(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const h = () => {
      setIsAdmin(false);
      setShowSettings(false);
      setShowCameras(false);
    };
    window.addEventListener("unauthenticated", h);
    return () => window.removeEventListener("unauthenticated", h);
  }, []);

  const openCameras = useCallback(() => {
    setShowSettings(false);
    setTimeout(() => setShowCameras(true), 0);
  }, []);

  const openUsers = useCallback(() => {
    setShowSettings(false);
    setTimeout(() => setShowUsers(true), 0);
  }, []);

  const openTags = useCallback(() => {
    setShowTags(false);
    setTimeout(() => setShowTags(true), 0);
  }, []);

  useEffect(() => {
    const onOpen = () => openCameras();
    window.addEventListener("open-manage-cameras", onOpen);
    return () => window.removeEventListener("open-manage-cameras", onOpen);
  }, [openCameras]);

  useEffect(() => {
    const onOpen = () => openUsers();
    window.addEventListener("open-manage-users", onOpen);
    return () => window.removeEventListener("open-manage-users", onOpen);
  }, [openUsers]);

  useEffect(() => {
    const onOpen = () => openTags();
    window.addEventListener("open-manage-tags", onOpen);
    return () => window.removeEventListener("open-manage-tags", onOpen);
  }, [openTags]);

  useEffect(() => {
    if (typeof inCurrentPage === "number" && !Number.isNaN(inCurrentPage)) {
      setCurrentPage(inCurrentPage);
    }
  }, [inCurrentPage]);

  useEffect(() => {
    const pStart = toDate(inStartDateTime);
    const pEnd = toDate(inEndDateTime);
    const same = (a, b) => (a === null && b === null) || (a && b && a.getTime() === b.getTime());
    if (!same(start, pStart)) setStart(pStart);
    if (!same(end, pEnd)) setEnd(pEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inStartDateTime, inEndDateTime]);

  useEffect(() => {
    setCurrentFilters(inCurrentFilters || DEFAULT_FILTERS);
  }, [inCurrentFilters]);

  const canGoBack = currentPage > 1;
  const canGoNext = currentPage < noOfPages;
  const onNextPage = () => setCurrentPage((p) => Math.min(p + 1, noOfPages));
  const onPrevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const onPageSelect = (pageNo) => setCurrentPage(pageNo);

  useEffect(() => {
    pageChangeHandler?.(currentPage);
  }, [currentPage, pageChangeHandler]);

  const notifyRange = (s, e) => {
    setCurrentPage(1);
    dateRangeChangeHandler?.({ startDateTime: s || null, endDateTime: e || null });
  };
  const handleStartChange = (nextStart) => {
    const now = new Date();
    if (!nextStart) {
      setStart(null);
      notifyRange(null, end);
      return;
    }
    let s = new Date(nextStart);
    s.setSeconds(0, 0);
    if (s > now) s = new Date(now);
    let e = end;
    if (e && e < s) {
      const snapped = addMinutes(s, 15);
      e = snapped > now ? new Date(now) : snapped;
      setEnd(e);
    }
    setStart(s);
    notifyRange(s, e);
  };
  const handleEndChange = (nextEnd) => {
    const now = new Date();
    if (!nextEnd) {
      setEnd(null);
      notifyRange(start, null);
      return;
    }
    let e = new Date(nextEnd);
    e.setSeconds(0, 0);
    if (e > now) e = new Date(now);
    let s = start;
    if (s && e < s) {
      const snapped = addMinutes(s, 15);
      e = snapped > now ? new Date(now) : snapped;
    }
    setEnd(e);
    notifyRange(s, e);
  };

  const now = new Date();
  const startMaxDate = start ? (end ? (end > now ? now : end) : now) : end ? (end > now ? now : end) : now;
  const endMaxDate = now;
  const startMinTime = start ? startOfDay(start) : undefined;
  const startMaxTime = start
    ? (() => {
      let cap = end && sameDay(start, end) ? end : endOfDay(start);
      if (sameDay(start, now)) cap = new Date(Math.min(cap.getTime(), now.getTime()));
      return cap;
    })()
    : undefined;
  const endMinTime = end ? (start && sameDay(start, end) ? start : startOfDay(end)) : undefined;
  const endMaxTime = end
    ? (() => {
      let cap = endOfDay(end);
      if (sameDay(end, now)) cap = new Date(Math.min(cap.getTime(), now.getTime()));
      return cap;
    })()
    : undefined;

  const onResetAll = () => {
    setStart(null);
    setEnd(null);
    setCurrentPage(1);
    setCurrentFilters(DEFAULT_FILTERS);
    dateRangeChangeHandler?.({ startDateTime: null, endDateTime: null });
    vrmFilterChangeHandler?.("");
    filtersChangeHandler?.(DEFAULT_FILTERS);
  };

  // responsive pagination sizing
  const bp = useBreakpoint();
  const maxButtons = bp.full ? 9 : bp.medium ? 5 : 3;
  const { start: startPage, end: endPage } = getPageWindow(currentPage, noOfPages, maxButtons);
  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  const vrmActive = !!(inCurrentVRMFilter && String(inCurrentVRMFilter).trim() !== "");
  const panelOn = isPanelFiltersActive(currentFilters);
  const filtersOn = vrmActive || panelOn;

  // ────────────────────────────────────────────────────────────────────────────
  // UI
  // ────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <header className={styles.navbar}>
        {/* LEFT: brand acts as reset */}
        <div
          className={styles.brandBlock}
          role="button"
          tabIndex={0}
          title="Reset filters and date range"
          onClick={onResetAll}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onResetAll()}
        >
          <img src={eye} alt="WatchGrid" className={styles.eyeLogo} />
          <div className={styles.brandCopy}>
            <div className={styles.brandName}>WATCHGRID</div>
            <div className={styles.tagline}>ANPR Vehicle Intelligence</div>
          </div>
        </div>

        {/* MIDDLE: date pickers */}
        <div className={styles.dateRow}>
          <div className={styles.dateField}>
            <DatePicker
              className={`${styles.dtInput} dt-input`}
              selected={start}
              onChange={handleStartChange}
              selectsStart
              startDate={start}
              endDate={end}
              showTimeSelect
              timeIntervals={15}
              shouldCloseOnSelect
              dateFormat="dd/MM/yyyy HH:mm"
              locale="en-GB"
              placeholderText="Start"
              isClearable
              maxDate={startMaxDate}
              {...(start ? { minTime: startMinTime, maxTime: startMaxTime } : {})}
            />
          </div>
          <div className={styles.dateField}>
            <DatePicker
              className={`${styles.dtInput} dt-input`}
              selected={end}
              onChange={handleEndChange}
              selectsEnd
              startDate={start}
              endDate={end}
              minDate={start || undefined}
              showTimeSelect
              timeIntervals={15}
              dateFormat="dd/MM/yyyy HH:mm"
              locale="en-GB"
              placeholderText="End"
              isClearable
              maxDate={endMaxDate}
              {...(end ? { minTime: endMinTime, maxTime: endMaxTime } : {})}
            />
          </div>
        </div>

        {/* RIGHT: actions + pagination */}
        <div className={styles.actionBlock}>


          <nav className={styles.paginationBar} aria-label="Table pagination">
            <button className={styles.pageBtn} onClick={onPrevPage} disabled={!canGoBack} aria-label="Previous page">
              <span aria-hidden>‹</span>
            </button>

            {noOfPages > 1 && (!bp.compact || currentPage > 2) && (
              <button
                onClick={() => onPageSelect(1)}
                className={`${styles.pageBtn} ${currentPage === 1 ? styles.activeBtn : ""}`}
                aria-current={currentPage === 1 ? "page" : undefined}
              >
                <span className={styles.pageText}>1</span>
              </button>
            )}

            {startPage > 2 && <span className={styles.ellipsis}>…</span>}

            {pageNumbers
              .filter((n) => !(noOfPages > 1 && n === 1))
              .map((pageNo) => (
                <button
                  key={pageNo}
                  onClick={() => onPageSelect(pageNo)}
                  className={`${styles.pageBtn} ${pageNo === currentPage ? styles.activeBtn : ""}`}
                  aria-current={pageNo === currentPage ? "page" : undefined}
                >
                  <span className={styles.pageText}>{pageNo}</span>
                </button>
              ))}

            {bp.compact && <span className={styles.ofTotal} aria-hidden>/ {noOfPages}</span>}

            <button className={styles.pageBtn} onClick={onNextPage} disabled={!canGoNext} aria-label="Next page">
              <span aria-hidden>›</span>
            </button>
          </nav>


          <div className={styles.quickActions}>
            {/* (Home button removed) */}

            <button
              type="button"
              ref={alertBtnRef}
              className={`${styles.iconTextBtn} ${showAlertMgr ? styles.iconTextBtnActive : ""}`}
              onClick={() => setShowAlertMgr(true)}
              title="Manage Alert List"
              aria-haspopup="dialog"
            >
              <img src={alertIcon} alt="" width={18} height={18} />
              <span className={styles.btnLabel}>Alerts</span>
            </button>

            <button
              type="button"
              ref={filterBtnRef}
              className={`${styles.iconTextBtn} ${showFilters ? styles.iconTextBtnActive : ""}`}
              onClick={() => setShowFilters((v) => !v)}
              aria-expanded={showFilters}
              aria-controls="filters-panel"
              aria-pressed={filtersOn || undefined}
              title={filtersOn ? "Filters (active)" : "Filters"}
            >
              <img src={filtersOn ? filterSolidIcon : filterOutlineIcon} alt="" width={18} height={18} />
              <span className={styles.btnLabel}>Filters</span>
            </button>

            <button
              type="button"
              ref={settingsBtnRef}
              className={`${styles.iconTextBtn} ${showSettings ? styles.iconTextBtnActive : ""}`}
              onClick={() => setShowSettings(true)}
              aria-haspopup="dialog"
              title="Settings"
              aria-label="Open settings"
            >
              <img src={settingsIcon} alt="" width={18} height={18} />
              <span className={styles.btnLabel}>Settings</span>
            </button>
          </div>



        </div>

        {/* keep these inside header if you like; portals below handle positioning */}

      </header>
      {showCameras &&
        createPortal(<CameraPanel open={showCameras} onClose={() => setShowCameras(false)} />, document.body)}
      {showUsers &&
        createPortal(<UserPanel open={showUsers} onClose={() => setShowUsers(false)} />, document.body)}
      {showTags &&
        createPortal(<TagPanel open={showTags} onClose={() => setShowTags(false)} />, document.body)}

      {/* PORTALS: render overlays into body to avoid sticky/backdrop issues */}
      {showAlertMgr &&
        createPortal(<AlertManager onClose={() => setShowAlertMgr(false)} />, document.body)}
      {showFilters &&
        createPortal(
          <FilterPanel
            anchorRef={filterBtnRef}
            initialVRM={inCurrentVRMFilter || ""}
            initialFilters={currentFilters}
            onApply={(vrmFromPanel, nextFilters) => {
              setCurrentPage(1);
              filtersChangeHandler?.(nextFilters);
              vrmFilterChangeHandler?.(vrmFromPanel);
              setCurrentFilters(nextFilters);
              setShowFilters(false);
            }}
            onClose={() => setShowFilters(false)}
          />,
          document.body
        )}
      {showSettings &&
        createPortal(
          <SettingsPanel
            isAdmin={isAdmin}
            anchorRef={settingsBtnRef}
            onManageCameras={openCameras}
            onManageUsers={openUsers}
            onLogout={async () => {
              await doLogout();
              window.location.reload();
            }}
            onClose={() => setShowSettings(false)}
          />,
          document.body
        )}
    </>
  );



};


export default Pagination;
